import React, { useCallback, useMemo, useState } from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Expander from '@amzn/meridian/expander';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import IntervalSummaryHeatmap from './IntervalSummaryHeatmap';
import OpportunityTimeline from './OpportunityTimeline';
import SegmentedControl from './SegmentedControl';
import { classifyIntervalStatus } from './LaborAvailabilityChartModel';
import {
  buildVetVtoSummary,
  formatDuration,
  hasSignal,
  seriesMaxAbs,
  subtractSeries,
  tintFor,
  toLaborHours
} from './laborOpportunitiesModel';
import { TINT_RGB } from '../../utils/laborOpportunityConstants';
import { TEXTS } from '../../utils/constants';
import './shared/flowPlannerShared.css';

const VIEW_MODES = {
  hc: { label: 'Headcount', headerLabel: 'Headcount', decimals: 1 },
  laborHrs: { label: 'Labor Hours', headerLabel: 'Labor Hours', decimals: 2 }
};

const VIEW_MODE_OPTIONS = Object.entries(VIEW_MODES).map(([value, { label }]) => ({ value, label }));

const formatTo = (decimals) => (value) => Number(value ?? 0).toFixed(decimals);

const buildSlots = (intervals, statuses) => {
  if (!intervals?.length) return [];
  return intervals.map((iv, idx) => ({
    primary: iv.name || `P${idx + 1}`,
    secondary: `${iv.startLabel || ''}–${iv.endLabel || ''}`,
    tertiary: formatDuration(iv.endTime - iv.startTime),
    indicatorStatus: statuses?.[idx]
  }));
};

const renderGapCell = (value, formatter) => {
  const num = Number(value || 0);
  if (Math.abs(num) < 1e-9) return <span className="fp-summary-value-zero">—</span>;
  const isShortage = num < 0;
  return (
    <span className={`gap-pill ${isShortage ? 'gap-pill--shortage' : 'gap-pill--surplus'}`}>
      {`${isShortage ? '−' : '+'}${formatter(Math.abs(num))}`}
    </span>
  );
};

const LaborOpportunities = ({
  requiredHCPerInterval,
  availableHCPerInterval,
  opportunityHCPerInterval,
  requiredLaborHoursPerInterval,
  paidHoursPerInterval,
  laborOpportunities,
  intervals
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [viewMode, setViewMode] = useState('hc');
  const isLaborHrs = viewMode === 'laborHrs';

  const series = useMemo(() => {
    const required = isLaborHrs ? requiredLaborHoursPerInterval : requiredHCPerInterval;
    const available = isLaborHrs ? toLaborHours(availableHCPerInterval, paidHoursPerInterval) : availableHCPerInterval;
    const vet = isLaborHrs
      ? toLaborHours(opportunityHCPerInterval?.vet, paidHoursPerInterval)
      : opportunityHCPerInterval?.vet;
    const vto = isLaborHrs
      ? toLaborHours(opportunityHCPerInterval?.vto, paidHoursPerInterval)
      : opportunityHCPerInterval?.vto;
    const gap = subtractSeries(available, required);
    return { required, available, gap, vet, vto };
  }, [
    isLaborHrs,
    requiredHCPerInterval,
    requiredLaborHoursPerInterval,
    availableHCPerInterval,
    opportunityHCPerInterval,
    paidHoursPerInterval
  ]);

  const { decimals, headerLabel } = VIEW_MODES[viewMode];
  const formatValue = useMemo(() => formatTo(decimals), [decimals]);
  const renderSignedGap = useCallback((value) => renderGapCell(value, formatValue), [formatValue]);

  const intervalStatuses = useMemo(
    () =>
      classifyIntervalStatus(
        requiredHCPerInterval,
        availableHCPerInterval,
        opportunityHCPerInterval?.vet,
        opportunityHCPerInterval?.vto
      ),
    [requiredHCPerInterval, availableHCPerInterval, opportunityHCPerInterval]
  );

  const slots = useMemo(() => buildSlots(intervals, intervalStatuses), [intervals, intervalStatuses]);

  const rows = useMemo(() => {
    const result = [];
    if (hasSignal(series.required)) {
      result.push({ label: 'Required', dotKind: 'required', values: series.required });
    }
    if (hasSignal(series.available)) {
      result.push({ label: 'Available', dotKind: 'available', values: series.available });
    }
    if (hasSignal(series.gap)) {
      result.push({
        label: 'Gap',
        dotKind: 'gap',
        emphasize: true,
        values: series.gap,
        renderValue: renderSignedGap
      });
    }
    if (hasSignal(series.vet)) {
      const max = seriesMaxAbs(series.vet);
      result.push({
        label: 'VET',
        dotKind: 'vet',
        values: series.vet,
        cellTint: (value) => tintFor(value, max, TINT_RGB.VET)
      });
    }
    if (hasSignal(series.vto)) {
      const max = seriesMaxAbs(series.vto);
      result.push({
        label: 'VTO',
        dotKind: 'vto',
        values: series.vto,
        cellTint: (value) => tintFor(value, max, TINT_RGB.VTO)
      });
    }
    return result;
  }, [series, renderSignedGap]);

  const summary = useMemo(() => buildVetVtoSummary(laborOpportunities), [laborOpportunities]);
  const hasRecommendations = summary.vetCount > 0 || summary.vtoCount > 0;

  return (
    <div className="fp-theme">
      <Column spacing="300" alignmentHorizontal="stretch" width="100%">
        <Expander
          open={isExpanded}
          onChange={setIsExpanded}
          title="VET/VTO Recommendations"
          type="inline"
          spacingInset="300">
          {hasRecommendations ? (
            <Column spacing="100" alignmentHorizontal="stretch" width="100%">
              <Row spacing="300" alignmentVertical="center" alignmentHorizontal="justify" width="100%">
                <Row spacing="200" alignmentVertical="center">
                  {summary.vetCount > 0 && <Tag type="error">{`VET · ${summary.vetCount} (${summary.vetHC} HC)`}</Tag>}
                  {summary.vtoCount > 0 && (
                    <Tag type="success">{`VTO · ${summary.vtoCount} (${summary.vtoHC} HC)`}</Tag>
                  )}
                </Row>
                <SegmentedControl
                  value={viewMode}
                  options={VIEW_MODE_OPTIONS}
                  onChange={setViewMode}
                  ariaLabel="View as headcount or labor hours"
                />
              </Row>
              <Box spacingInset="300 none none none">
                {rows.length ? (
                  <IntervalSummaryHeatmap
                    headerLabel={headerLabel}
                    slots={slots}
                    rows={rows}
                    formatValue={formatValue}
                  />
                ) : (
                  <Text type={TEXTS.T2} color="secondary">
                    No headcount data available for this plan.
                  </Text>
                )}
              </Box>
              <OpportunityTimeline
                laborOpportunities={laborOpportunities}
                intervals={intervals}
                intervalStatuses={intervalStatuses}
              />
            </Column>
          ) : (
            <Text type={TEXTS.T2} color="secondary">
              No VET/VTO recommendations for this plan.
            </Text>
          )}
        </Expander>
      </Column>
    </div>
  );
};

export default LaborOpportunities;
